import { useCombobox, Combobox, Popover, UnstyledButton } from "@mantine/core";
import { useHover } from "@mantine/hooks";
import { useState } from "react";

import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { useUpdatePartSubType } from "#modules/parts/view/useUpdatePartSubType";
import { useUpdatePartType } from "#modules/parts/view/useUpdatePartType";
import { getSelectablePartTypeOptions, iconForPartType } from "#shared/helpers/partTypeOptions";
import { partTypesWordingMap } from "#shared/helpers/PartTypesWordingMap";
import { Icon } from "#shared/icons/Icon";
import { Group } from "#shared/view/components/Group";
import { Chip } from "#shared/view/designSystem/Chip";
import { PartTypeSchema, PartTypeType } from "shared";

import { QuoteRequestLineDto } from "../infra/fetchQuoteRequestLine.api";

const SubTypeOption = ({
  partType,
  subType,
  onSelect,
}: {
  partType: PartTypeType;
  subType: string;
  onSelect: (partType: PartTypeType, subType: string, e: React.MouseEvent) => void;
}) => {
  const { hovered, ref } = useHover<HTMLButtonElement>();

  return (
    <UnstyledButton
      ref={ref}
      p="3px"
      display="block"
      w="100%"
      fz="xs"
      style={{
        borderRadius: "var(--mantine-radius-xs)",
        background: hovered ? "var(--mantine-color-default-hover)" : undefined,
      }}
      onClick={(e) => onSelect(partType, subType, e)}
    >
      {subType}
    </UnstyledButton>
  );
};

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  part: PartDto;
  allSubTypes: string[];
  disabled?: boolean;
};

export const PartTypeChip = ({ part, quoteRequestLine, allSubTypes, disabled = false }: Props) => {
  const { updatePartType } = useUpdatePartType({
    partId: part.id,
    quoteRequestLine,
  });
  const { updatePartSubType } = useUpdatePartSubType({
    partId: part.id,
    quoteRequestLineId: quoteRequestLine.id,
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const disableCombobox = part.type === PartTypeSchema.enum.ASSEMBLY || disabled;

  const handleTypeSelect = (val: string) => {
    const selectedPartType = val as PartTypeType;
    if (selectedPartType === part.type) {
      return;
    }

    updatePartType({ newPartType: selectedPartType, newPartSubType: null });
    combobox.closeDropdown();
  };

  const handleSubTypeSelect = (partType: PartTypeType, subType: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSubMenuOpen(false);
    if (part.type !== partType) {
      updatePartType({ newPartType: partType, newPartSubType: subType });
    } else if (subType !== part.subType) {
      updatePartSubType({ newPartSubType: subType });
    }
    combobox.closeDropdown();
  };

  const hasSubTypes = allSubTypes.length > 0;

  const options = getSelectablePartTypeOptions(part.type).map((item) => {
    const hasOptionSubTypes = item.value === PartTypeSchema.enum.PROFILE && hasSubTypes;

    const optionContent = (
      <Group gap="xxs" justify={hasOptionSubTypes ? "space-between" : undefined}>
        <Group gap="xxs">
          <Icon name={item.icon} size={12} />
          {item.label}
        </Group>
        {hasOptionSubTypes && <Icon name="chevronRight" size={10} />}
      </Group>
    );

    if (hasOptionSubTypes) {
      return (
        <Popover
          key={item.value}
          opened={isSubMenuOpen}
          position="right-start"
          withinPortal
          offset={0}
          transitionProps={{ duration: 0 }}
        >
          <Popover.Target>
            <Combobox.Option
              value={item.value}
              p="3px"
              onMouseEnter={() => setIsSubMenuOpen(true)}
              onMouseLeave={() => setIsSubMenuOpen(false)}
            >
              {optionContent}
            </Combobox.Option>
          </Popover.Target>
          <Popover.Dropdown
            p={4}
            onMouseEnter={() => setIsSubMenuOpen(true)}
            onMouseLeave={() => setIsSubMenuOpen(false)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {allSubTypes.sort().map((subType) => (
              <SubTypeOption
                key={subType}
                partType={item.value}
                subType={subType}
                onSelect={handleSubTypeSelect}
              />
            ))}
          </Popover.Dropdown>
        </Popover>
      );
    }

    return (
      <Combobox.Option value={item.value} key={item.value} p="3px">
        {optionContent}
      </Combobox.Option>
    );
  });

  if (!part.type) {
    return null;
  }

  return (
    <Combobox
      store={combobox}
      width={"max-content"}
      position="bottom-start"
      withArrow
      size="xs"
      withinPortal={false}
      onOptionSubmit={handleTypeSelect}
      disabled={disableCombobox}
    >
      <Combobox.Target>
        <Chip
          size="xs"
          icon={<Icon name={iconForPartType[part.type]} size={12} />}
          checked
          color="red.2"
          autoContrast
          onClick={() => combobox.toggleDropdown()}
        >
          {partTypesWordingMap[part.type]}
          {part.type === PartTypeSchema.enum.PROFILE && part.subType ? ` · ${part.subType}` : ""}
        </Chip>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
